/**
 * What each Feature Matrix row IS and where to find it — the hover card on a
 * feature name in Retail Circle Admin → Feature Matrix (Alex, 2026-10-28).
 *
 * Keyed by App\Constants\Features key. A row with no entry simply has no card.
 *
 *   what  one or two sentences, plain language
 *   path  where it lives, outermost first, in the app's own labels — the
 *         "Find it" line. Empty = no screen of its own (or not built).
 *
 * The picture above the text is a real screenshot (featurePreview/shots/<key>.jpg,
 * made by scripts/feature-previews/capture.cjs). Every path here was checked
 * against those screenshots on 2026-10-28. A new matrix row should get an
 * entry here and a shot in the capture script.
 */

const NOT_BUILT = 'Not built yet — hidden from every store.';

export const FEATURE_GUIDE = {
  // ── Operations ──────────────────────────────────────────────────────────
  inventory_listing_management: {
    what: 'Create, edit and manage stock listings.',
    path: ['Listings', 'Manage Listings'],
  },
  limit_listings: {
    what: 'How many listings a store can create per plan month. At the limit, creating a listing shows a limit notice.',
    path: ['Listings', '+ New Stock'],
  },
  sync_chrono24: {
    what: 'Puts a listing on the store\'s Chrono24 account — the Chrono24 switch in a listing card\'s marketplaces menu.',
    path: ['Listings', 'Listing card', 'Marketplaces', 'Chrono24'],
  },
  sync_chrono24_multistore: {
    what: 'A second (or third) Chrono24 account for the same store, e.g. Chrono24 Europe in EUR, each with its own stock feed.',
    path: ['Settings', 'Owner Admin', 'Marketplace Accounts', 'Chrono24 Europe'],
  },
  sync_watchfinder: {
    what: 'Connects the store\'s WatchFinder shop so listings are sent to WatchFinder.',
    path: ['Settings', 'Owner Admin', 'Marketplace Accounts', 'WatchFinder'],
  },
  sync_whatsapp_catalog: {
    what: 'A feed URL the store pastes into Meta Commerce Manager so its live stock appears in its WhatsApp catalogue.',
    path: ['Settings', 'Owner Admin', 'Catalog Feeds', 'WhatsApp / Meta catalog'],
  },
  sync_google_shopping: {
    what: 'A feed URL the store pastes into Google Merchant Center so its live stock appears in Google Shopping.',
    path: ['Settings', 'Owner Admin', 'Catalog Feeds', 'Google Shopping'],
  },
  sync_meta_catalog: { what: `Listings to Instagram / Facebook shops. ${NOT_BUILT}`, path: [] },
  sync_ebay: {
    what: 'Connects the store\'s eBay seller account (Sign in with eBay) to list items on eBay. DAO Watches only until it launches.',
    path: ['Settings', 'Owner Admin', 'Marketplace Accounts', 'eBay'],
  },
  sync_ecommerce: { what: `Two-way sync with Shopify, WooCommerce or Magento. ${NOT_BUILT}`, path: [] },
  sync_google_sheet: { what: `Listings exported to a Google Sheet. ${NOT_BUILT}`, path: [] },
  custom_integration: { what: `Bespoke integration for one merchant. ${NOT_BUILT}`, path: [] },
  competitive_pricing_check: {
    what: 'Nightly check of each listing\'s price against the market; brands with listings out of range show as cards at the top of Listings.',
    path: ['Listings', 'Manage Listings', '… listings out of range'],
  },
  ai_listing_creation: { what: `AI writes the listing photos and description. ${NOT_BUILT}`, path: [] },
  reuse_listing_images: {
    what: 'Tick folders of photos from earlier listings of the same model to reuse them instead of re-uploading.',
    path: ['Listings', 'Edit', 'Listing Images', 'Folders'],
  },
  lookbook_generation: {
    what: 'Builds a shareable lookbook from selected listings.',
    path: ['Listings', '+ New Lookbook'],
  },
  request_lookbook_generation: {
    what: 'Lookbook built from selected order requests, to send to the client.',
    path: ['Orders', 'Lookbooks'],
  },
  core_inventory_management: {
    what: 'Keeps the models a store always wants in stock and alerts when one becomes available — the Core List, Ad List and Sales Pipeline tabs.',
    path: ['Stock Alert', 'Core List'],
  },
  geo_arbitrage: {
    what: 'Ranks buy-here/sell-there price gaps for the same reference across regions, with profit, confidence and live route prices.',
    path: ['Stock Alert', 'Arbitrages'],
  },
  share_to_broadcast: {
    what: 'Opens WhatsApp with the item\'s text already filled in.',
    path: ['Listings', 'Listing card', '⋯ menu', 'Send via WhatsApp'],
  },
  wa_shared_number: {
    what: 'Posts the store\'s items to its WhatsApp dealer groups from a linked number, with saved group lists, daily limits and an activity log; also adds "Share to Broadcast" on listings, orders and Kanban.',
    path: ['WhatsApp Broadcast'],
  },
  voice_dictation: {
    what: 'Speak a listing or request and the form fills itself in — the microphone beside "+ New Stock" (and on Orders).',
    path: ['Listings', 'Microphone'],
  },
  shipping_labels_tracking: {
    what: 'Connects carrier accounts (e.g. DHL Express) to create shipping labels and tracking links. DAO Watches only until it launches.',
    path: ['Settings', 'Owner Admin', 'Shipping Providers'],
  },
  repairs_workshop: {
    what: 'Mark a watch as out for repair, with dates and the watchmaker holding it. DAO Watches only until it launches.',
    path: ['Listings', 'Edit', 'Internal Details', 'Cost', 'In Repair'],
  },
  direct_wa_message: { what: `Send WhatsApp messages to clients from inside the app. ${NOT_BUILT}`, path: [] },

  // ── Sourcing ────────────────────────────────────────────────────────────
  requests_capture: {
    what: 'Record a client\'s buy (sourcing) or sell enquiry and carry it through to a quote.',
    path: ['Orders', '+ Sourcing Enquiry / + Sell Enquiry'],
  },
  kanban_list_view: {
    what: 'Order and sell requests as a board of stages you drag between.',
    path: ['Kanban'],
  },
  wa_group_ingestion: {
    what: 'Reads the store\'s WhatsApp groups and turns buy requests into leads matched to your stock — the Leads page.',
    path: ['Leads'],
  },
  supplier_pricing_capture: {
    what: 'Prices recorded from suppliers and marketplaces for a model; they feed trade pricing and PriceIQ.',
    path: ['Listings', 'Product Database', 'Pricing', 'Trading history'],
  },
  outside_network_search: { what: `Source or sell beyond the store's own network. ${NOT_BUILT}`, path: [] },
  quotes_proposals: {
    what: 'Every supplier quote for the watch, with the one this deal uses ticked.',
    path: ['Orders', 'Request', 'Select Order Quote', 'Quotes'],
  },
  priceiq_quotes: {
    what: 'Suggested sell price, consign range and market low/mid/high beside an open quote.',
    path: ['Orders', 'Request', 'Select Order Quote', 'Quote', 'PriceIQ'],
  },
  buyops_ch24_private_sellers: {
    what: 'Overnight list of private sellers on Chrono24 asking below the 3-month trade median, with a suggested offer.',
    path: ['Deals', 'Private sellers'],
  },
  deals_auctions: {
    what: 'Live WatchCollecting auctions: the ones ending within 6 hours, and every live lot by closing day, with countdown, current bid and the trade price.',
    path: ['Deals', 'Auctions'],
  },
  buyops_underpriced_alerts: {
    what: 'Trade and Chrono24 prices found 10% or more below the trade median.',
    path: ['Deals', 'Underpriced'],
  },
  deals_contact_seller: {
    what: 'Sends a Chrono24 enquiry to the private seller in one click, and tracks it in the Watchlist.',
    path: ['Deals', 'Private sellers', 'Contact seller'],
  },
  trade_feed: {
    what: 'Every dealer WhatsApp message from our seats, searchable and filterable by country, region and group. (The assistant was taken off this page on 2026-10-16.)',
    path: ['Trade Feed'],
  },

  // ── CRM ─────────────────────────────────────────────────────────────────
  crm_client_management: {
    what: 'Client records — turnover, last purchase and contact details — that orders, quotes and requests point at.',
    path: ['Users', 'Clients'],
  },
  wishlist: { what: `Client wishlists. ${NOT_BUILT}`, path: [] },
  kyc_verification: {
    what: 'Ask a client to verify their ID; pending clients show under "ID Verification Pending".',
    path: ['Users', 'Client', 'KYC'],
  },
  crm_events_email: { what: `Automatic birthday / anniversary emails to clients. ${NOT_BUILT}`, path: [] },
  transactional_emails: {
    what: 'Whether the store sends client emails at all (quotes, invoices, agreements, payment receipts). No switch on screen: off means they are skipped silently. Pictured: the client-facing emails it covers.',
    path: ['Settings', 'Retail Circle Admin', 'Emails', 'Client facing'],
  },
  branded_transactional_emails: {
    what: 'Client emails go out with the store\'s own logo, name and From/Reply-To addresses.',
    path: ['Settings', 'Owner Admin', 'Email Branding'],
  },

  // ── Pricing & data ──────────────────────────────────────────────────────
  trade_price_data: { what: `Trade price history per model. ${NOT_BUILT}`, path: [] },
  market_price_data: { what: `Market (retail) price history per model. ${NOT_BUILT}`, path: [] },
  data_validation_screen: {
    what: 'Shows the dealer WhatsApp messages the AI matched to this model and what it extracted, so you can check it.',
    path: ['Listings', 'Product Database', 'AI-Extraction'],
  },

  // ── Trust ───────────────────────────────────────────────────────────────
  ai_authentication: { what: `AI checks whether a watch is genuine. ${NOT_BUILT}`, path: [] },
  watch_register_check: { what: `Stolen-watch check against the Watch Register. ${NOT_BUILT}`, path: [] },
  reference_database: {
    what: 'Shared catalogue of models and specs; pick the product a listing is for.',
    path: ['Listings', 'Edit', 'Product'],
  },

  // ── Payments ────────────────────────────────────────────────────────────
  stripe_payments: {
    what: 'Take a payment by Stripe link, paid into the store\'s own Stripe.',
    path: ['Orders', 'Request', 'Payments', 'Payment method', 'Stripe Link'],
  },
  stripe_terminal: { what: `Take card payments in person on a terminal. ${NOT_BUILT}`, path: [] },
  manual_payments: {
    what: 'Record a deposit or payment taken outside the app — Transfer, Card, Cash or USDT.',
    path: ['Orders', 'Request', 'Payments', 'Deposit paid'],
  },
  internal_costs: {
    what: 'Buying price (Details tab) and extra costs such as polishing, accessories, shipping and travel (Cost tab); they feed profit and P&L.',
    path: ['Listings', 'Edit', 'Internal Details', 'Cost'],
  },
  accounting_pnl_reconciliation: {
    what: 'Monthly P&L per watch sold, plus Reconciliation and the Accounting tab on Analytics.',
    path: ['Analytics', 'P&L'],
  },
  commissions_split: {
    what: 'Split a deal\'s commission between reps or partners by percentage.',
    path: ['Orders', 'Request', 'Commissions'],
  },
  escrow: { what: `Hold the buyer's money until delivery. ${NOT_BUILT}`, path: [] },
  purchase_consignment_agreements: {
    what: 'Generate and send the consignment or purchasing agreement for the seller to sign.',
    path: ['Orders', 'Sell request', 'Consignment Agreement / Purchasing Agreement'],
  },
  purchase_order_generation: {
    what: 'PDF purchase order made by Retail Circle, no Xero needed.',
    path: ['Orders', 'Sell request', 'Purchase Order'],
  },
  xero_purchase_orders: {
    what: 'Purchase order created in the store\'s Xero (organisation picked in Payments). Same section as the native one.',
    path: ['Orders', 'Sell request', 'Purchase Order'],
  },
  invoice_generation: {
    what: 'PDF invoice made by Retail Circle, no Xero needed.',
    path: ['Orders', 'Request', 'Invoice'],
  },
  xero_invoicing: {
    what: 'Invoice created in the store\'s Xero, with the VAT toggle. Same section as the native one.',
    path: ['Orders', 'Request', 'Invoice'],
  },
  vat_estimator: { what: `Estimates VAT owed on margin-scheme sales. ${NOT_BUILT}`, path: [] },

  // ── Platform ────────────────────────────────────────────────────────────
  multi_category: { what: `Sell categories other than watches. ${NOT_BUILT}`, path: [] },
  limit_seats: {
    what: 'How many people the account can have (open invites count); shown as the Team seats meter in Your Plan.',
    path: ['Settings', 'Owner Admin', 'Your Plan', 'Team seats'],
  },
  analytics_core: {
    what: 'The Dashboard (My Day, Store, Reports) and the Analytics page — store, sales rep and exec reports plus the daily email.',
    path: ['Dashboard'],
  },
  analytics_marketing: {
    what: 'Website traffic from Google Analytics: sessions, channels, top pages and countries.',
    path: ['Analytics', 'Marketing'],
  },
  mcp_access: {
    what: 'Approve or revoke who can connect Claude (the Retail Circle MCP servers) to the data, with an audit log. Staff only until it is rebuilt per store.',
    path: ['Settings', 'Retail Circle Admin', 'MCP Access'],
  },
  uptime_monitor: { what: 'Error and beta-report board across the apps. Retail Circle staff only today.', path: ['Uptime'] },
  custom_branding: { what: `The store's logo and colours inside the app. ${NOT_BUILT}`, path: [] },
  mobile_app: { what: `The iOS app for merchants. ${NOT_BUILT}`, path: [] },
  bespoke_storefront: { what: `A public website built for the store. ${NOT_BUILT}`, path: [] },
};
